import { Component, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { WebService } from '../services/web/web.service';
import { SpinnerService } from '../services/spinner/spinner.service';
import { ToastService } from '../services/notification/toast.service';
import { UserPermissionsService } from '../services/user-permissions.service';
import { TranslateService } from '@ngx-translate/core';
import { RefundReceiptDialogComponent } from '../customer-invoice-detail/refund-receipt-dialog/refund-receipt-dialog.component';
import { InvoiceRefundView } from '../customer-invoice-detail/invoice-refund-view.model';
import { environment } from '../../environments/environment';
import { catchError, forkJoin, of } from 'rxjs';

type DateRange = 'today' | 'last7Days' | 'last30Days' | 'currentMonth' | 'previousMonth' | 'custom' | null;
type RefundStatus = 'SUCCEEDED' | 'PENDING' | 'FAILED';

interface RefundReportFilters {
  dateRange: DateRange;
  startDate: Date | null;
  endDate: Date | null;
  partnerId: string | null;
  customerId: string | null;
  customerName: string;
  customerLastName: string;
  transactionId: string;
  authorizationNumber: string;
  pageIndex: number;
  pageSize: number;
}

interface RefundReportRow {
  customerName: string;
  invoiceId: string;
  partnerInvoiceCode: string;
  currency: string;
  originalAmount: number;
  refundAmount: number;
  authorizationNumber: string;
  refundAuthorizationNumber: string;
  refundedAt: Date | null;
  status: RefundStatus;
  raw: any;
}

@Component({
  selector: 'app-refund-report',
  templateUrl: './refund-report.component.html',
  styleUrls: ['./refund-report.component.scss'],
  standalone: false,
})
export class RefundReportComponent implements OnInit {
  readonly displayedColumns = ['customer', 'partnerInvoiceCode', 'originalAmount', 'refundAmount', 'authorizationNumber', 'refundAuthorizationNumber', 'refundedAt', 'status', 'actions'];
  readonly pageSizeOptions = [20, 50, 100];
  readonly dateRanges: { value: DateRange; label: string }[] = [
    { value: 'today', label: 'refundReport.filters.today' },
    { value: 'last7Days', label: 'refundReport.filters.last7Days' },
    { value: 'last30Days', label: 'refundReport.filters.last30Days' },
    { value: 'currentMonth', label: 'refundReport.filters.currentMonth' },
    { value: 'previousMonth', label: 'refundReport.filters.previousMonth' },
    { value: 'custom', label: 'refundReport.filters.custom' },
  ];

  filters = this.createDefaultFilters();
  partners: any[] = [];
  customers: any[] = [];
  partnerControl = new FormControl<any>('');
  customerControl = new FormControl<any>({ value: '', disabled: true });
  rows: RefundReportRow[] = [];
  totalRecords = 0;
  dateRangeError = false;
  hasRefundReportPermission = false;
  isLoading = false;
  loadError = false;
  isExporting = false;
  private readonly refundApiUrl = `${environment.backendUrl}/refund`;

  constructor(
    private webService: WebService,
    private userPermissionsService: UserPermissionsService,
    private translate: TranslateService,
    private dialog: MatDialog,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private spinner: SpinnerService,
    private toast: ToastService,
  ) {}

  ngOnInit(): void {
    this.hasRefundReportPermission = this.userPermissionsService.hasPermission('onboarding.reports.refunds');
    if (!this.hasRefundReportPermission) return;

    this.applyQueryParamsToState();

    this.spinner.open();
    const params = this.webService.buildRequestParams().set('parents', 'false').set('parent', '-1');
    this.webService.get(`${this.webService.HOST}/partner/user`, params).subscribe({
      next: (response: any) => {
        this.partners = response?.result ?? response ?? [];
        this.restorePartnerFromQuery();
        this.loadRefunds(false);
      },
      error: () => {
        this.partners = [];
        this.loadRefunds(false);
      },
    });
  }

  onDateRangeChange(): void {
    if (this.filters.dateRange !== 'custom') {
      this.filters.startDate = null;
      this.filters.endDate = null;
    }
    this.dateRangeError = false;
  }

  search(): void {
    if (!this.hasRefundReportPermission) return;
    this.dateRangeError = !this.isValidDateRange();
    if (this.dateRangeError) return;

    this.filters.pageIndex = 0;
    this.syncUrlQueryParams();
    this.loadRefunds();
  }

  clearFilters(): void {
    if (!this.hasRefundReportPermission) return;
    this.filters = this.createDefaultFilters();
    this.partnerControl.setValue('');
    this.customerControl.reset({ value: '', disabled: true });
    this.customers = [];
    this.dateRangeError = false;
    this.syncUrlQueryParams();
    this.loadRefunds();
  }

  onPage(event: PageEvent): void {
    if (!this.hasRefundReportPermission) return;
    this.filters.pageIndex = event.pageIndex;
    this.filters.pageSize = event.pageSize;
    this.syncUrlQueryParams();
    this.loadRefunds();
  }

  canExport(): boolean {
    return this.hasRefundReportPermission && this.totalRecords > 0 && !this.isExporting;
  }

  canViewRefundReceipt(row: RefundReportRow): boolean {
    return this.hasRefundReportPermission && row.status === 'SUCCEEDED';
  }

  openRefundReceipt(row: RefundReportRow): void {
    if (!this.canViewRefundReceipt(row)) return;

    const raw = row.raw ?? {};
    const invoiceId = Number(row.invoiceId);
    if (!Number.isFinite(invoiceId)) {
      this.openRefundReceiptDialog(row, raw);
      return;
    }

    this.spinner.open();
    const paymentParams = this.webService.buildRequestParams(undefined, 'm', { pageIndex: -1, pageSize: -1 });
    forkJoin({
      invoiceResponse: this.webService.get(`${this.webService.HOST}/invoice/${invoiceId}`).pipe(catchError(() => of(null))),
      paymentsResponse: this.webService.get(`${this.webService.HOST}/payment/list/${invoiceId}`, paymentParams).pipe(catchError(() => of(null))),
    }).subscribe(({ invoiceResponse, paymentsResponse }: any) => {
      this.spinner.close();
      this.openRefundReceiptDialog(row, raw, invoiceResponse?.result, paymentsResponse?.result);
    });
  }

  private openRefundReceiptDialog(row: RefundReportRow, raw: any, invoiceResponse: any = null, payments: any[] = []): void {
    const invoice = invoiceResponse ?? raw.invoice ?? raw.payment?.invoice ?? {
      id: row.invoiceId,
      customer: {
        name_customer: raw.customerName ?? '',
        lastName_customer: raw.customerLastName ?? '',
        code_customer: raw.customerCode ?? '',
      },
      customer_has_plan: raw.customer_has_plan ?? {},
    };
    const successfulPayment = payments.find(payment => String(payment?.resultStatus_payment) === '00');
    const payment = successfulPayment ? this.preparePayment(successfulPayment) : raw.payment ?? {
      transactionId: raw.transactionId ?? raw.transaction_id ?? '',
      description_payment: raw.description ?? raw.paymentDescription ?? '',
      amount_payment: row.originalAmount,
      transactionDate: raw.transactionDate ?? raw.transaction_date ?? '',
    };
    const refund: InvoiceRefundView = {
      id: Number(raw.id ?? 0),
      refundType: raw.refundType ?? raw.refund_type ?? null,
      amount: raw.amount ?? row.refundAmount,
      currency: raw.currency ?? invoice.currency_invoice ?? null,
      status: row.status,
      createdAt: raw.createdAt ?? null,
      resolvedAt: raw.resolvedAt ?? raw.refundedAt ?? null,
      processorRefundId: raw.processorRefundId ?? raw.processor_refund_id ?? null,
      authorizationNumber: raw.refundAuthorizationNumber ?? raw.refund_authorization_number ?? null,
      systemTraceNumber: raw.refundSystemTraceNumber ?? raw.refund_system_trace_number ?? null,
      hostDate: raw.refundHostDate ?? raw.refund_host_date ?? null,
      hostTime: raw.refundHostTime ?? raw.refund_host_time ?? null,
    };
    const refundType = String(refund.refundType ?? '').toUpperCase();

    this.dialog.open(RefundReceiptDialogComponent, {
      width: '680px',
      maxWidth: 'calc(100vw - 32px)',
      maxHeight: 'calc(100vh - 32px)',
      data: {
        invoice,
        payment,
        refund,
        refundTypeLabel: refundType ? this.translate.instant(`customerInvoiceDetail.refunds.types.${refundType.toLowerCase()}`) : null,
        refundStatusLabel: this.translate.instant(`customerInvoiceDetail.refunds.statuses.${row.status.toLowerCase()}`),
        refundAuthorizationUnavailableLabel: this.translate.instant('customerInvoiceDetail.refundReceipt.unavailable'),
        currency: refund.currency ?? 'USD',
      },
    });
  }

  private preparePayment(payment: any): any {
    return {
      ...payment,
      description_payment: payment?.result_description ?? payment?.description_payment ?? '',
      transactionDate: payment?.transactionDate ?? this.formatPaymentTransactionDate(payment?.hostDate, payment?.hostTime),
    };
  }

  private formatPaymentTransactionDate(hostDate: unknown, hostTime: unknown): string {
    const date = String(hostDate ?? '');
    if (!/^\d{8}$/.test(date)) return '';

    const isYearFirst = /^\d{4}(0[1-9]|1[0-2])/.test(date);
    const year = isYearFirst ? date.slice(0, 4) : date.slice(4, 8);
    const month = isYearFirst ? date.slice(4, 6) : date.slice(0, 2);
    const day = isYearFirst ? date.slice(6, 8) : date.slice(2, 4);
    const time = String(hostTime ?? '');
    const formattedTime = /^\d{6}$/.test(time) ? `${time.slice(0, 2)}:${time.slice(2, 4)}:${time.slice(4, 6)}` : '00:00:00';
    return `${year}-${month}-${day} ${formattedTime}`;
  }

  exportExcel(): void {
    this.exportAll(async (rows) => {
      const ExcelJS = await import('exceljs');
      const Workbook = (ExcelJS as any).Workbook ?? (ExcelJS as any).default?.Workbook;
      const workbook = new Workbook();
      const worksheet = workbook.addWorksheet(this.translate.instant('refundReport.title'));
      worksheet.columns = this.exportHeaders().map((header) => ({ header, width: 22 }));
      rows.forEach(row => worksheet.addRow(this.exportValues(row)));
      const buffer = await workbook.xlsx.writeBuffer();
      this.download(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), 'refund-report.xlsx');
    });
  }

  exportPdf(): void {
    if (!this.canExport()) return;

    const printWindow = window.open('', '_blank', 'width=800,height=900');
    if (!printWindow) {
      this.toast.showError(this.translate.instant('refundReport.messages.pdfPopupBlocked'));
      return;
    }

    printWindow.document.write(`<p>${this.escapeHtml(this.translate.instant('refundReport.results.loading'))}</p>`);
    printWindow.document.close();
    this.exportAll(async (rows) => {
      const title = this.escapeHtml(this.translate.instant('refundReport.title'));
      const headers = this.exportHeaders().map(header => `<th>${this.escapeHtml(header)}</th>`).join('');
      const body = rows.map(row => `<tr>${this.exportValues(row).map(value => `<td>${this.escapeHtml(value)}</td>`).join('')}</tr>`).join('');
      printWindow.document.open();
      printWindow.document.write(`<html><head><title>${title}</title><style>body{font-family:Arial;padding:24px}table{border-collapse:collapse;width:100%;font-size:12px}th,td{border:1px solid #ddd;padding:8px;text-align:left}th{background:#155724;color:#fff}</style></head><body><h1>${title}</h1><table><thead><tr>${headers}</tr></thead><tbody>${body}</tbody></table></body></html>`);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
    });
  }

  get filteredPartners(): any[] {
    const search = typeof this.partnerControl.value === 'string' ? this.partnerControl.value.trim().toLocaleLowerCase() : '';
    return search ? this.partners.filter((partner) => this.displayPartner(partner).toLocaleLowerCase().includes(search)) : this.partners;
  }

  get filteredCustomers(): any[] {
    const search = typeof this.customerControl.value === 'string' ? this.customerControl.value.trim().toLocaleLowerCase() : '';
    return search ? this.customers.filter((customer) => this.displayCustomer(customer).toLocaleLowerCase().includes(search)) : this.customers;
  }

  displayPartner = (partner: any): string => partner?.name || partner?.name_partner || partner?.business_name || String(partner?.id ?? '');
  displayCustomer = (customer: any): string => `${customer?.name_customer ?? customer?.name ?? ''} ${customer?.lastName_customer ?? customer?.lastname_customer ?? ''}`.trim() || String(customer?.id ?? '');

  onPartnerInput(): void {
    this.filters.partnerId = null;
    this.clearCustomer();
  }

  selectPartner(partner: any, customerIdToRestore: string | null = null): void {
    if (!this.hasRefundReportPermission) return;
    if (!partner) return this.clearPartner();
    this.partnerControl.setValue(partner);
    this.filters.partnerId = String(partner.id);
    this.clearCustomer();
    if (customerIdToRestore) this.filters.customerId = customerIdToRestore;
    this.customerControl.enable({ emitEvent: false });
    const params = this.webService.buildRequestParams().set('pageIndex', '-1').set('pageSize', '-1');
    this.webService.get(`${this.webService.HOST}/partner/${partner.id}/customer`, params).subscribe({
      next: (response: any) => {
        this.customers = response?.result ?? response ?? [];
        if (customerIdToRestore) {
          const customer = this.customers.find((item) => String(item.id) === customerIdToRestore);
          if (customer) this.selectCustomer(customer);
        }
      },
      error: () => this.customers = [],
    });
  }

  clearPartner(): void {
    this.partnerControl.setValue('');
    this.filters.partnerId = null;
    this.clearCustomer();
  }

  onCustomerInput(): void {
    this.filters.customerId = null;
  }

  selectCustomer(customer: any): void {
    if (!customer) return this.clearCustomer();
    this.customerControl.setValue(customer);
    this.filters.customerId = String(customer.id);
  }

  clearCustomer(): void {
    this.filters.customerId = null;
    this.customers = [];
    this.customerControl.reset({ value: '', disabled: !this.filters.partnerId }, { emitEvent: false });
  }

  private isValidDateRange(): boolean {
    return this.filters.dateRange !== 'custom' || (!!this.filters.startDate && !!this.filters.endDate && this.filters.startDate <= this.filters.endDate);
  }

  private applyQueryParamsToState(): void {
    const query = this.activatedRoute.snapshot.queryParamMap;
    const dateRange = query.get('dateRange') as DateRange;
    const pageIndex = Number(query.get('pageIndex'));
    const pageSize = Number(query.get('pageSize'));
    if (['today', 'last7Days', 'last30Days', 'currentMonth', 'previousMonth', 'custom'].includes(dateRange as string)) {
      this.filters.dateRange = dateRange;
    }
    this.filters.startDate = this.parseDate(query.get('startDate'));
    this.filters.endDate = this.parseDate(query.get('endDate'));
    this.filters.partnerId = query.get('partnerId');
    this.filters.customerId = query.get('customerId');
    this.filters.customerName = query.get('customerName') ?? '';
    this.filters.customerLastName = query.get('customerLastName') ?? '';
    this.filters.transactionId = query.get('transactionId') ?? '';
    this.filters.authorizationNumber = query.get('authorizationNumber') ?? '';
    if (Number.isInteger(pageIndex) && pageIndex >= 0) this.filters.pageIndex = pageIndex;
    if (this.pageSizeOptions.includes(pageSize)) this.filters.pageSize = pageSize;
  }

  private restorePartnerFromQuery(): void {
    if (!this.filters.partnerId) return;
    const partner = this.partners.find((item) => String(item.id) === this.filters.partnerId);
    if (!partner) {
      this.filters.partnerId = null;
      this.filters.customerId = null;
      return;
    }
    const customerId = this.filters.customerId;
    this.selectPartner(partner, customerId);
  }

  private syncUrlQueryParams(): void {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {
        dateRange: this.filters.dateRange || null,
        startDate: this.filters.dateRange === 'custom' ? this.formatDate(this.filters.startDate) : null,
        endDate: this.filters.dateRange === 'custom' ? this.formatDate(this.filters.endDate) : null,
        partnerId: this.filters.partnerId || null,
        customerId: this.filters.customerId || null,
        customerName: this.filters.customerName || null,
        customerLastName: this.filters.customerLastName || null,
        transactionId: this.filters.transactionId || null,
        authorizationNumber: this.filters.authorizationNumber || null,
        pageIndex: this.filters.pageIndex || null,
        pageSize: this.filters.pageSize === 20 ? null : this.filters.pageSize,
      },
      replaceUrl: true,
    });
  }

  private parseDate(value: string | null): Date | null {
    if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  private loadRefunds(showSpinner = true): void {
    if (!this.hasRefundReportPermission) return;
    this.isLoading = true;
    this.loadError = false;
    if (showSpinner) this.spinner.open();
    this.webService.get(this.buildRefundUrl()).subscribe({
      next: (response: any) => {
        const data = response?.result ?? response ?? [];
        this.rows = (Array.isArray(data) ? data : data?.entities ?? []).map((refund: any) => this.mapRefund(refund));
        const pager = response?.pager ?? data?.pager ?? {};
        this.totalRecords = Number(pager.totalEntities ?? pager.total ?? this.rows.length);
        this.isLoading = false;
        this.loadError = false;
        this.spinner.close();
      },
      error: () => {
        this.isLoading = false;
        this.loadError = true;
        this.spinner.close();
        this.toast.showError(this.translate.instant('refundReport.results.loadError'));
      }
    });
  }

  private exportAll(onRows: (rows: RefundReportRow[]) => Promise<void>): void {
    if (!this.canExport()) return;
    this.isExporting = true;
    this.spinner.open();
    this.webService.get(this.buildRefundUrl(-1, -1)).subscribe({
      next: async (response: any) => {
        try {
          const data = response?.result ?? response ?? [];
          const rows = (Array.isArray(data) ? data : data?.entities ?? []).map((refund: any) => this.mapRefund(refund));
          await onRows(rows);
        } catch {
          this.toast.showError(this.translate.instant('refundReport.messages.exportError'));
        } finally {
          this.isExporting = false;
          this.spinner.close();
        }
      },
      error: () => {
        this.isExporting = false;
        this.spinner.close();
        this.toast.showError(this.translate.instant('refundReport.messages.exportError'));
      }
    });
  }

  private buildRefundUrl(pageIndex = this.filters.pageIndex, pageSize = this.filters.pageSize): string {
    const params = new URLSearchParams({
      pageIndex: String(pageIndex),
      pageSize: String(pageSize),
      sort: '-resolvedAt',
      origin: 'onboarding'
    });
    const dateQuery = this.getDateQuery();
    this.addParam(params, 'dateRange', dateQuery.dateRange);
    this.addParam(params, 'startDate', dateQuery.startDate);
    this.addParam(params, 'endDate', dateQuery.endDate);
    this.addParam(params, 'partnerId', this.filters.partnerId);
    this.addParam(params, 'customerId', this.filters.customerId);
    this.addParam(params, 'customerName', this.filters.customerName);
    this.addParam(params, 'customerLastName', this.filters.customerLastName);
    this.addParam(params, 'transactionId', this.filters.transactionId);
    this.addParam(params, 'authorizationNumber', this.filters.authorizationNumber);
    return `${this.refundApiUrl}?${params.toString()}`;
  }

  /** The refunds API accepts explicit dates, so quick options are converted to its custom range contract. */
  private getDateQuery(): { dateRange?: 'custom'; startDate?: string; endDate?: string } {
    if (!this.filters.dateRange) return {};

    const today = new Date();
    const currentDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    let start: Date | null = this.filters.startDate;
    let end: Date | null = this.filters.endDate;

    switch (this.filters.dateRange) {
      case 'today':
        start = currentDay;
        end = currentDay;
        break;
      case 'last7Days':
        start = new Date(currentDay);
        start.setDate(start.getDate() - 6);
        end = currentDay;
        break;
      case 'last30Days':
        start = new Date(currentDay);
        start.setDate(start.getDate() - 29);
        end = currentDay;
        break;
      case 'currentMonth':
        start = new Date(currentDay.getFullYear(), currentDay.getMonth(), 1);
        end = currentDay;
        break;
      case 'previousMonth':
        start = new Date(currentDay.getFullYear(), currentDay.getMonth() - 1, 1);
        end = new Date(currentDay.getFullYear(), currentDay.getMonth(), 0);
        break;
      case 'custom':
        break;
    }

    if (!start || !end) return {};
    const startDate = this.formatDate(start);
    const endDate = this.formatDate(end);
    if (!startDate || !endDate) return {};

    return {
      dateRange: 'custom',
      startDate,
      endDate,
    };
  }

  private addParam(params: URLSearchParams, name: string, value?: string | null): void {
    if (value?.trim()) params.set(name, value.trim());
  }

  private formatDate(value: Date | null): string | null {
    if (!value) return null;
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${value.getFullYear()}-${month}-${day}`;
  }

  private mapRefund(refund: any): RefundReportRow {
    const invoice = refund?.invoice ?? refund?.payment?.invoice ?? {};
    const customer = refund?.customer ?? invoice?.customer ?? {};
    const customerName = refund?.customerName ?? customer?.name_customer ?? customer?.name ?? '';
    const customerLastName = refund?.customerLastName ?? customer?.lastName_customer ?? customer?.lastname_customer ?? '';
    return {
      customerName: `${customerName} ${customerLastName}`.trim() || '-',
      invoiceId: String(refund?.invoiceId ?? refund?.invoice_id ?? invoice?.id ?? '-'),
      partnerInvoiceCode: String(refund?.partnerInvoiceCode || refund?.partner_invoice_code || invoice?.partnerInvoiceCode || invoice?.partner_invoice_code || '-'),
      currency: String(refund?.currency ?? invoice?.currency_invoice ?? 'USD'),
      originalAmount: Number(refund?.originalAmount ?? refund?.totalInvoice ?? invoice?.total_invoice ?? invoice?.total ?? 0),
      refundAmount: Number(refund?.refundAmount ?? refund?.amount ?? 0),
      authorizationNumber: refund?.authorizationNumber ?? refund?.authorization_number ?? refund?.payment?.authorization_number ?? '',
      refundAuthorizationNumber: refund?.refundAuthorizationNumber ?? refund?.refund_authorization_number ?? '',
      refundedAt: this.parseRefundDate(refund?.refundedAt ?? refund?.resolvedAt ?? refund?.createdAt),
      status: String(refund?.status ?? refund?.refundStatus ?? 'PENDING').toUpperCase() as RefundStatus,
      raw: refund,
    };
  }

  private exportHeaders(): string[] {
    return ['customer', 'invoiceId', 'partnerInvoiceCode', 'originalAmount', 'refundAmount', 'authorizationNumber', 'refundAuthorizationNumber', 'refundedAt', 'status']
      .map(key => this.translate.instant(`refundReport.table.${key}`));
  }

  private exportValues(row: RefundReportRow): string[] {
    return [row.customerName, row.invoiceId, row.partnerInvoiceCode, String(row.originalAmount), String(row.refundAmount), row.authorizationNumber || '-', row.refundAuthorizationNumber || '-', this.formatRefundDateForExport(row.refundedAt), this.translate.instant(`refundReport.status.${row.status}`)];
  }

  private parseRefundDate(value: unknown): Date | null {
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
    if (typeof value !== 'string') return null;

    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})$/);
    if (!match) {
      const isoDate = new Date(value);
      return Number.isNaN(isoDate.getTime()) ? null : isoDate;
    }

    const [, year, month, day, hours, minutes, seconds] = match.map(Number);
    const date = new Date(year, month - 1, day, hours, minutes, seconds);
    return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day
      && date.getHours() === hours && date.getMinutes() === minutes && date.getSeconds() === seconds
      ? date
      : null;
  }

  private formatRefundDateForExport(date: Date | null): string {
    if (!date) return '-';
    const pad = (value: number) => String(value).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  }

  private download(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }

  private escapeHtml(value: string): string {
    return value.replace(/[&<>'"]/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[char] ?? char));
  }

  private createDefaultFilters(): RefundReportFilters {
    return { dateRange: null, startDate: null, endDate: null, partnerId: null, customerId: null, customerName: '', customerLastName: '', transactionId: '', authorizationNumber: '', pageIndex: 0, pageSize: 20 };
  }
}
